import { useRouter, useNuxtApp } from 'nuxt/app'
import { ref } from 'vue'
import type { Ref } from 'vue'
// @ts-ignore
import type { RouterProps } from 'instantsearch.js/es/middlewares'
import { COLLECTIONS } from '~/lib/search/typesense'

function stripUndefined(obj: Record<string, any>) {
  return Object.fromEntries(
    Object.entries(obj).filter(([_k, v]) => v !== undefined)
  )
}
export const useCategoryRouter = () => {
  const router = useRouter()
  const app = useNuxtApp()
  const productIndex = COLLECTIONS.products
  const { productFilters, productSorters } = useProductFilterStore()

  /**
   * @param state e.g. { tarvike_products_dev: { range: { finalPrice.inclTax: '1672:' }, ... } }
   * @returns e.g. { hinta: '1672~' }
   */
  const stateToQuery_Range = (state: any) => {
    const refinements = state[productIndex]?.range as Record<string, string>
    if (!refinements) return {}
    return Object.fromEntries(
      Object.entries(refinements).map(([key, value]) => {
        const newKey = productFilters.find(filter => filter.attributePath === key)?.urlParam || key
        return [newKey, value.split(':').join('~')]
      })
    )
  }

  /**
   * @param state e.g. { tarvike_products_dev: { refinementList: { technicalSpecifications.size.value: ['M', 'L', 'S'] } } }
   * @returns e.g. { koko: 'M~L~S' }
   */
  const stateToQuery_RefinementList = (state: any) => {
    const refinements = state[productIndex]?.refinementList as Record<string, string[]>
    if (!refinements) return {}
    return Object.fromEntries(
      Object.entries(refinements).map(([key, value]) => {
        const newKey = productFilters.find(filter => filter.attributePath === key)?.urlParam || key
        return [newKey, value.join('~')]
      })
    )
  }

  /**
   * @param state e.g. { tarvike_products_dev: { toggle: { stock.stockStatus: 'kylla' } } }
   * @returns e.g. { varastossa: 'kylla' }
   */
  const stateToQuery_Toggle = (state: any) => {
    const refinements = state[productIndex]?.toggle as Record<string, string[]>
    if (!refinements) return {}
    return Object.fromEntries(
      Object.entries(refinements).map(([key, _value]) => {
        const filter = productFilters.find(filter => filter.attributePath === key)
        const newKey = filter?.urlParam || key
        return [newKey, 'kylla']
      })
    )
  }

  /**
   * @param state e.g. { tarvike_products_dev: { sortBy: 'tarvike_products_dev_replica_sort_name_default_asc' } }
   * @returns e.g. { jarjesta: 'nimen-mukaan' }
   */
  const stateToQuery_SortBy = (state: any) => ({ jarjesta: productSorters.find(sorter => productIndex + sorter.indexAffix === state[productIndex]?.sortBy)?.urlValue })

  /**
   * @param state e.g. { tarvike_products_dev: { page: 2, ... } }
   * @returns e.g. { sivu: 2 }
   */
  const stateToQuery_Page = (state: any) => ({ sivu: state[productIndex]?.page })

  /**
   * @param state e.g. { tarvike_products_dev: { page: 2, refinementList: { ... }, ... } }
   * @returns e.g. { sivu: 2, jarjesta: 'nimen-mukaan', koko: 'M~L~S' }
   */
  const stateToQuery = (state: any) => {
    const current = router.currentRoute.value
    return stripUndefined({
      q: current.path === '/haku' ? state[productIndex]?.query : undefined,
      ...stateToQuery_Page(state),
      ...stateToQuery_SortBy(state),
      ...stateToQuery_RefinementList(state),
      ...stateToQuery_Toggle(state),
      ...stateToQuery_Range(state)
    })
  }

  /**
   * @param query e.g. { koko: 'M~L~S' }
   * @returns e.g. { technicalSpecifications.size.value: ['M', 'L', 'S'] }
   */
  const queryToState_RefinementList = (query: any) => {
    return Object.fromEntries(
      Object.entries(query).filter(([k]) => productFilters.map(filter => filter.urlParam).includes(k) && productFilters.find(filter => filter.urlParam === k)?.widget === 'refinementList')
        .map(([k, v]) => {
          const filter = productFilters.find(filter => filter.urlParam === k)
          return [filter?.attributePath || k, (v as string).split('~')]
        })
    )
  }

  /**
   * @param query e.g. { alennuksessa: 'kylla' }
   * @returns e.g. { stock.stockStatus: 'kylla' } (value is corrected for Algolia query, in this case to 'inStock')
   */
  const queryToState_Toggle = (query: any) => {
    return Object.fromEntries(
      Object.entries(query).filter(([k]) => productFilters.map(filter => filter.urlParam).includes(k) && productFilters.find(filter => filter.urlParam === k)?.widget === 'toggle')
        .map(([k, v]) => {
          const filter = productFilters.find(filter => filter.urlParam === k)
          return [filter?.attributePath || k, v]
        })
    )
  }

  /**
   * @param query e.g. { hinta: '1672~' }
   * @returns e.g. { finalPrice.inclTax: '1672:' }
   */
  const queryToState_Range = (query: any) => {
    return Object.fromEntries(
      Object.entries(query).filter(([k]) => productFilters.map(filter => filter.urlParam).includes(k) && productFilters.find(filter => filter.urlParam === k)?.widget === 'range')
        .map(([k, v]) => {
          const filter = productFilters.find(filter => filter.urlParam === k)
          return [filter?.attributePath || k, (v as string).split('~').join(':')]
        })
    )
  }

  /**
   * @param query e.g. { jarjesta: 'nimen-mukaan' }
   * @returns e.g. 'tarvike_products_dev_replica_sort_name_default_asc'
   */
  const queryToState_SortBy = (query: any) => {
    const affix = (productSorters.find(sorter => sorter.urlValue === query.jarjesta)?.indexAffix || '')
    return affix ? productIndex + affix : undefined
  }

  /**
   * @param query e.g. { sivu: '2' }
   * @returns e.g. 2
   */
  const queryToState_Page = (query: any) => {
    return query.sivu
  }

  const queryToState = (query: any) => {
    const current = router.currentRoute.value
    return {
      [productIndex]: stripUndefined({
        query: current.path === '/haku' ? query?.q : undefined,
        page: queryToState_Page(query),
        sortBy: queryToState_SortBy(query),
        refinementList: queryToState_RefinementList(query),
        toggle: queryToState_Toggle(query),
        range: queryToState_Range(query)
      })
    }
  }

  const algoliaRouter: Ref<Pick<Required<RouterProps>, 'router'>> = ref({
    stateMapping: {
      stateToRoute(uiState: any) {
        return stateToQuery(uiState)
      },
      routeToState(routeState: any) {
        return queryToState(routeState)
      }
    },
    router: {
      read(): any {
        return router.currentRoute.value.query
      },
      write(routeState: any) {
        const current = router.currentRoute.value
        const nextQuery = current.path === '/haku' ? stripUndefined({ ...current.query, ...routeState }) : routeState
        if (this.createURL(nextQuery) === this.createURL(this.read())) {
          return
        }
        router.push({ query: nextQuery })
      },
      createURL(routeState: any) {
        const current = router.currentRoute.value
        const query = current.path === '/haku' ? stripUndefined({ ...current.query, ...routeState }) : routeState
        return router.resolve({
          query
        }).href
      },
      onUpdate(cb: any) {
        if (typeof window === 'undefined') return
        // @ts-ignore
        this._removeAfterEach = router.afterEach((to, from) => {
          if (to.path === from.path) cb(this.read())
        })
        app.hook('page:finish', () => {
          cb(this.read())
        })
      },
      dispose() {
        if (typeof window === 'undefined') {
          return
        }
        // @ts-ignore
        if (this._removeAfterEach) {
          // @ts-ignore
          this._removeAfterEach()
        }
      }
    }
  })

  return algoliaRouter
}
